import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  CareerPathResponse,
  mapCareerPathResponseToCareerPath,
  ContentNugget,
  BackrsPost,
  BackrsUser,
  ExternalProfile,
  externalProfileTransform,
} from '@backrs_ui/common-ui';

import { generatedContentApi } from '@backrs_ui/grow-ui/src/redux/generatedContent/generatedContent.api';
import { fetchWrapper as fetch } from '@backrs_ui/grow-ui/src/utils/fetch/fetchWrapper';

const ENVIRONMENT_URL = process.env.REACT_APP_API_BASEURL;

export const fetchOneContentNuggetBySlug = createAsyncThunk(
  'contentCompetency/fetchOneContentNuggetBySlug',
  async (params: { slug: string }, { rejectWithValue }) => {
    const url = new URL(`${ENVIRONMENT_URL}/content-nugget`);
    const filter = JSON.stringify({ slug: params.slug });

    url.searchParams.append('filter', filter);

    try {
      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      if (!data || !data.length) {
        return rejectWithValue('No content nugget found');
      }

      return data[0] as ContentNugget;
    } catch (error) {
      return rejectWithValue(`Failed to fetch content competencies with error: ${error}`);
    }
  },
);

export const searchForContentScholarship = createAsyncThunk(
  'search/searchForContentScholarship',
  async (params: { search_term: string }, { rejectWithValue }) => {
    try {
      // Fetch the scholarship by ID
      const url = new URL(`${ENVIRONMENT_URL}/content-search/scholarship/${params.search_term}`);

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      const scholarshipAsNugget: ContentNugget = {
        id: data.id,
        title: data.name,
        description: data.description,
        slug: data.donor,
        format: 'scholarships',
        star_rating: 0,
        value_rating: 0,
        variant: 'scholarships',
        scholarship_info: data,
        is_browsable: false,
      };

      return scholarshipAsNugget;
    } catch (error) {
      return rejectWithValue(`Failed to fetch scholarship with error: ${error}`);
    }
  },
);

export const searchForContentProfileExternalById = createAsyncThunk(
  'search/searchForContentProfileExternalById',
  async (params: { search_term: string }, { rejectWithValue }) => {
    try {
      // Fetch the profile by ID
      const url = new URL(
        `${ENVIRONMENT_URL}/content-search/profile-external/${params.search_term}`,
      );

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data: ExternalProfile = await response.json();

      const profileExternalAsNugget: ContentNugget = externalProfileTransform(data);

      return profileExternalAsNugget;
    } catch (error) {
      return rejectWithValue(`Failed to fetch profile external with error: ${error}`);
    }
  },
);

export const searchForContentInternship = createAsyncThunk(
  'search/searchForContentInternship',
  async (params: { search_term: string }, { rejectWithValue }) => {
    try {
      // Fetch the scholarship by ID
      const url = new URL(`${ENVIRONMENT_URL}/content-search/internship/${params.search_term}`);

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      const internshipAsNugget: ContentNugget = {
        id: data.id,
        title: data.role_title,
        description: data.ai_description,
        slug: data.id,
        content_nugget_theme: 'Opportunity',
        format: 'internship',
        star_rating: 0,
        value_rating: 0,
        variant: 'internship',
        internship_info: data,
        is_browsable: false,
      };

      return internshipAsNugget;
    } catch (error) {
      return rejectWithValue(`Failed to fetch internship with error: ${error}`);
    }
  },
);

export const searchForCareerPathById = createAsyncThunk(
  'search/searchForCareerPathById',
  async (params: { id: string }, { rejectWithValue }) => {
    try {
      // Fetch the career path by ID
      const url = new URL(`${ENVIRONMENT_URL}/career-path/${params.id}`);

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const career_path = (await response.json()) as CareerPathResponse;

      const careerPathAsNugget: ContentNugget = {
        id: career_path.id,
        title: career_path.name,
        description: career_path.description,
        slug: `${career_path.id}`,
        format: 'enrichment',
        star_rating: 0,
        value_rating: 0,
        variant: 'career-path',
        is_browsable: false,
        career_path_info: mapCareerPathResponseToCareerPath(career_path),
      };

      return careerPathAsNugget;
    } catch (error) {
      return rejectWithValue(`Failed to fetch career-path with error: ${error}`);
    }
  },
);

// profile
export const searchForProfileInternalById = createAsyncThunk(
  'search/searchForProfileInternalById',
  async (params: { id: string }, { rejectWithValue }) => {
    try {
      // Fetch the profile by ID
      const url = new URL(`${ENVIRONMENT_URL}/content-search/profile-internal/${params.id}`);

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const profileInternal = (await response.json()) as BackrsUser;

      const profileInternalAsNugget: ContentNugget = {
        id: profileInternal.id,
        title: `Connect with and Review ${profileInternal.first_name}'s Profile`,
        description: profileInternal.bio || '',
        slug: `${profileInternal.id}`,
        format: 'profile',
        star_rating: 0,
        value_rating: 0,
        variant: 'profile-internal',
        is_browsable: false,
        profile_info: profileInternal,
      };

      return profileInternalAsNugget;
    } catch (error) {
      return rejectWithValue(`Failed to fetch profile internal with error: ${error}`);
    }
  },
);

// post
export const searchForPostById = createAsyncThunk(
  'search/searchForPostById',
  async (params: { id: string }, { rejectWithValue }) => {
    try {
      // Fetch the profile by ID
      const url = new URL(`${ENVIRONMENT_URL}/posts/${params.id}`);

      const response = await fetch(url.toString());

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const post = (await response.json()) as BackrsPost;

      const postAsNugget: ContentNugget = {
        id: post.id,
        title: `${post.author.first_name}'s Thread`,
        description: post.body,
        slug: `${post.id}`,
        format: 'post',
        star_rating: 0,
        value_rating: 0,
        variant: 'post',
        is_browsable: false,
        content_nugget_theme: 'Community',
        post_info: post,
      };

      return postAsNugget;
    } catch (error) {
      return rejectWithValue(`Failed to fetch post with error: ${error}`);
    }
  },
);

// generated-content
export const searchForGeneratedContentByUuid = createAsyncThunk(
  'search/searchForGeneratedContentByUuid',
  async (params: { uuid: string }, { rejectWithValue, dispatch }) => {
    try {
      const generatedContent = await dispatch(
        generatedContentApi.endpoints.getGeneratedContent.initiate(params.uuid),
      ).unwrap();

      if (!generatedContent.id) {
        return rejectWithValue('No generated content found for UUID: ' + params.uuid);
      }

      const generatedContentAsNugget: ContentNugget = {
        id: generatedContent.id,
        title: generatedContent.title,
        description: '',
        slug: `${generatedContent.id}`,
        star_rating: 0,
        value_rating: 0,
        variant: 'generated-content',
        content_nugget_theme: 'For You',
        is_browsable: false,
        generated_content_info: generatedContent,
      };

      return generatedContentAsNugget;
    } catch (error) {
      return rejectWithValue(`Failed to fetch generated content with error: ${error}`);
    }
  },
);
